import React from 'react';
import { Box, Typography, Link, Stack, Button } from '@mui/material';
import Section from './Section';

const MY_NAME = 'Anastasiya Masalava';

// Bolds my name in an author list.
const formatAuthors = (authors) =>
  authors.split(', ').map((name, index) => (
    <React.Fragment key={index}>
      {index > 0 && ', '}
      {name === MY_NAME ? <strong>{name}</strong> : name}
    </React.Fragment>
  ));

// image: path under public/ (e.g. '/papers/eidseg.jpg'), or null to show a placeholder.
const papers = [
  {
    title: 'EIDSeg: A Pixel-Level Semantic Segmentation Dataset for Post-Earthquake Damage Assessment from Social Media Images',
    authors: 'Huili Huang, Chengeng Liu, Danrong Zhang, Shail Patel, Anastasiya Masalava, Sagar Sadak, Parisa Babolhavaeji, Weihong Low, Mahdi Roozbahani, J. David Frost',
    venue: 'AAAI Conference on Artificial Intelligence (AAAI), 2026',
    image: '/papers/eidseg.jpg',
    links: [
      { label: 'Paper', url: 'https://ojs.aaai.org/index.php/AAAI/article/view/41201' },
      { label: 'arXiv', url: 'https://arxiv.org/abs/2511.06456' },
      { label: 'Dataset', url: 'https://huggingface.co/datasets/huilihuang413/EIDSeg' },
      { label: 'Code', url: 'https://github.com/HUILIHUANG413/EIDSeg' },
    ],
  },
  {
    title: 'Enhancing the Fidelity of Social Media Image Data Sets in Earthquake Damage Assessment',
    authors: 'Huili Huang, Danrong Zhang, Anastasiya Masalava, M. Mahdi Roozbahani, Nimisha Roy, J. David Frost',
    venue: 'Earthquake Spectra, 2025',
    image: '/papers/earthquake_spectra.jpg',
    links: [
      { label: 'Paper', url: 'https://doi.org/10.1177/87552930251335649' },
      { label: 'Dataset', url: 'https://doi.org/10.17603/ds2-yj8p-hs62' },
      { label: 'Code', url: 'https://github.com/HUILIHUANG413/Earthquake-Infrastracture-Damage' },
    ],
  },
];

const blogPosts = [
  {
    title: 'Predicting Disease Trajectories: A Temporal Graph Learning Approach',
    venue: 'Stanford CS224W Blog',
    date: 'Dec 2025',
    url: 'https://medium.com/@giseller_79885/predicting-disease-trajectories-a-temporal-graph-learning-approach-2a00e42906d4',
  },
  {
    title: 'Automating Financial Variance Analysis with DBSCAN Clustering and LightGBM Classification',
    venue: 'DoorDash Engineering Blog',
    date: 'Sep 2025',
    url: 'https://careersatdoordash.com/blog/part-2-doordash-2025-summer-intern-projects/',
  },
  {
    title: 'Applying Vision Transformers to Detect and Prevent Photo Recycling Fraud',
    venue: 'DoorDash Engineering Blog',
    date: 'Aug 2024',
    url: 'https://careersatdoordash.com/blog/part-1-doordash-2024-summer-intern-projects/',
  },
];

// Paper image; links to the paper and zooms slightly on hover.
const Thumbnail = ({ src, alt, href }) => (
  <Box
    component={href ? 'a' : 'div'}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'block',
      width: { xs: 220, sm: 180 },
      aspectRatio: '4 / 3',
      flexShrink: 0,
      borderRadius: 1.5,
      overflow: 'hidden',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'grey.100',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)', transform: 'translateY(-2px)' },
      '&:hover img': { transform: 'scale(1.04)' },
    }}
  >
    {src && (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s' }}
      />
    )}
  </Box>
);

const Publications = () => {
  return (
    <Section id="publications" title="Publications">
      <Stack spacing={4}>
        {papers.map((paper) => (
          <Stack key={paper.title} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }}>
            <Thumbnail src={paper.image} alt={paper.title} href={paper.links[0]?.url} />
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {paper.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {formatAuthors(paper.authors)}
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                {paper.venue}
              </Typography>
              <Stack direction="row" spacing={1}>
                {paper.links.map((link) => (
                  <Button
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    sx={{ py: 0, minWidth: 0 }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Typography component="h3" variant="h6" sx={{ mt: 5, mb: 1.5 }}>
        Selected Blog Posts
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {blogPosts.map((post) => (
          <Typography component="li" key={post.url} variant="body2" sx={{ mb: 0.75 }}>
            <Link href={post.url} target="_blank" rel="noopener noreferrer">
              {post.title}
            </Link>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' '}· {post.venue}, {post.date}
            </Box>
          </Typography>
        ))}
      </Box>
    </Section>
  );
};

export default Publications;
